import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ReportView from "../components/ReportView";
import ExportMenu from "../components/ExportMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/reports/${id}`, { withCredentials: true });
        setReport(r.data);
        if (r.data?.share_token) {
          const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
          setShare({
            token: r.data.share_token,
            url: `${base}/share/report/${r.data.share_token}`,
          });
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load report");
      }
    })();
  }, [id]);

  const onShare = async () => {
    setSharing(true);
    try {
      const r = await axios.post(`${API}/reports/${id}/share`, { expires_in_days: 90 },
                                 { withCredentials: true });
      setShare({ token: r.data.token, url: r.data.share_url });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to share");
    } finally {
      setSharing(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this report? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/reports/${id}`, { withCredentials: true });
      nav("/reports");
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (error) return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="text-sm text-red-700">{error}</div>
      <Link to="/reports" className="text-emerald-700 text-sm font-mono underline mt-2 inline-block">← Back to reports</Link>
    </div>
  );
  if (!report) return <div className="max-w-3xl mx-auto px-4 py-6 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-3">
        <Link to="/reports" className="text-xs font-mono text-zinc-500 hover:text-zinc-700">← All reports</Link>
        <div className="flex items-center gap-2">
          <ExportMenu
            testId="report-export"
            items={[{ label: "Saved report", csvUrl: `${API}/reports/${id}/export.csv`, xlsxUrl: `${API}/reports/${id}/export.xlsx` }]}
          />
          <button onClick={onShare} disabled={sharing}
                  className="px-3 py-1.5 rounded border border-zinc-300 text-xs font-mono hover:bg-zinc-50 disabled:opacity-50"
                  data-testid="share-report-btn">
            {sharing ? "Creating…" : (share ? "Re-share" : "Share")}
          </button>
          <button onClick={onDelete}
                  className="px-3 py-1.5 rounded border border-zinc-300 text-xs font-mono hover:bg-red-50 text-red-700"
                  data-testid="delete-report-btn">
            Delete
          </button>
        </div>
      </div>
      {share && (
        <div className="mb-4 border border-emerald-300 bg-emerald-50 rounded-lg p-3 text-xs font-mono text-emerald-900"
             data-testid="share-link-banner">
          Public share link: <a href={share.url} target="_blank" rel="noreferrer" className="underline break-all">{share.url}</a>
        </div>
      )}
      <ReportView report={report} />
    </div>
  );
}
